import { useState, useEffect } from "react";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../firebaseConfig";

export default function FeedbackModal({ open, onClose, isPage = false }: { open?: boolean; onClose?: () => void; isPage?: boolean }) {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const [overall, setOverall] = useState<string>("");
  const [design, setDesign] = useState<string>("");
  const [clarity, setClarity] = useState<string>("");
  const [improveNext, setImproveNext] = useState<string>("");
  const [canContact, setCanContact] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [showThankYou, setShowThankYou] = useState<boolean>(false);

  const disabled = !(overall && design && clarity) || improveNext.length > 300 || (canContact && !/^\S+@\S+\.\S+$/.test(email));

  // Reset form when modal opens
  useEffect(() => {
    if (open || isPage) {
      setOverall("");
      setDesign("");
      setClarity("");
      setImproveNext("");
      setCanContact(false);
      setEmail("");
      setShowThankYou(false);
    }
  }, [open, isPage]);

  async function submit() {
    const user = auth.currentUser;
    
    // Collect browser/environment info
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    await addDoc(collection(db, "feedback"), {
      createdAt: serverTimestamp(),
      sessionId: crypto.randomUUID(),
      path: location.pathname,
      device: /Mobi|Android/i.test(userAgent) ? "mobile" : "desktop",
      
      // Survey responses
      q_overall: overall,
      q_design: design,
      q_clarity: clarity,
      improveNext: improveNext.trim(),
      
      // Contact preference (explicit opt-in)
      canContact,
      contactEmail: canContact ? email : null,
      
      // Silent user info (if logged in)
      userId: user?.uid || null,
      userEmail: user?.email || null,
      userName: user?.displayName || null,
      userPhotoURL: user?.photoURL || null,
      
      // Silent environment/browser info
      screenResolution: screenRes,
      viewportSize: viewportSize,
      browser: userAgent,
      language: language,
      timezone: timezone,
      referrer: document.referrer || null
    });
    
    // Show thank you message
    setShowThankYou(true);
    
    // Auto-close after 2 seconds
    setTimeout(() => {
      if (onClose) onClose();
    }, 2000);
  }

  if (!open && !isPage) return null;

  const content = (
    <div className={`w-full max-w-md rounded-2xl bg-[var(--card-bg,#111)] text-[var(--fg,#fff)] p-5 shadow-xl ${isPage ? "mx-auto my-8" : ""}`}>
      {showThankYou ? (
        <div className="text-center py-8">
          <div className="text-5xl mb-4">✓</div>
          <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
          <p className="text-sm opacity-80">Your feedback helps us improve.</p>
        </div>
      ) : (
        <>
          <h2 className="text-xl font-semibold mb-4">Feedback</h2>
      {[
        { label: "Overall, how would you rate our website?", val: overall, set: setOverall, opts: ["Excellent", "Good", "Average", "Poor"] },
        { label: "How appealing is the visual design?", val: design, set: setDesign, opts: ["Very appealing", "Somewhat appealing", "Not at all appealing"] },
        { label: "How clear is the content?", val: clarity, set: setClarity, opts: ["Very clear", "Somewhat clear", "Not at all clear"] },
      ].map((q, i) => (
        <div key={i} className="mb-4">
          <p className="mb-2 text-sm opacity-90">{q.label}</p>
          <div className="flex flex-wrap gap-2">
            {q.opts.map(o => (
              <button
                key={o}
                onClick={() => q.set(o)}
                className={`px-3 py-1.5 rounded-full border text-sm ${q.val === o ? "bg-[var(--accent,#7c3aed)] text-white border-transparent" : "border-white/20"}`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="mb-3">
        <label className="mb-2 block text-sm opacity-90">
          One thing we should improve next? <span className="opacity-60">(max 300)</span>
        </label>
        <textarea
          value={improveNext}
          onChange={e => setImproveNext(e.target.value)}
          maxLength={300}
          rows={3}
          className="w-full rounded-lg bg-white/5 border border-white/15 px-3 py-2 resize-none"
          placeholder="e.g., timeline spacing on mobile"
        />
      </div>
      <div className="mb-5 flex items-center gap-3">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={canContact} onChange={e => setCanContact(e.target.checked)} />
          <span>Can we contact you about your feedback?</span>
        </label>
        {canContact && (
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="flex-1 rounded-lg bg-white/5 border border-white/15 px-3 py-2"
            placeholder="email (optional)"
          />
        )}
      </div>
      <div className="flex justify-end gap-2">
        {!isPage && (
          <button onClick={onClose} className="px-3 py-2 rounded-lg border border-white/20">
            Cancel
          </button>
        )}
        <button
          disabled={disabled}
          onClick={submit}
          className={`px-4 py-2 rounded-lg ${disabled ? "opacity-50 cursor-not-allowed" : "bg-[var(--accent,#7c3aed)] text-white"}`}
        >
          Submit
        </button>
      </div>
        </>
      )}
    </div>
  );

  if (isPage) return content;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center" role="dialog" aria-modal="true">
      {content}
    </div>
  );
}
